import { useState } from 'react';
import type { BadgeCriterion, BadgeDraft } from '@contracts';
import { IconPickerPopover } from './IconPickerPopover';

type CriterionTemplate = BadgeCriterion['kind'];

const TEMPLATE_LABELS: Record<CriterionTemplate, string> = {
  habit_completions: 'Complete a habit N times',
  checkin_streak_weeks: 'Check-in streak of N weeks',
  accepted_recalibrations: 'Accept N adjustments',
  clean_weeks: 'N clean weeks on an avoid habit',
  weekly_reviews: 'Complete N weekly reviews',
  action_completions: 'Complete N objective actions',
};

/**
 * The two templates Rewards v1 can actually evaluate (phase-2a-rewards.md
 * RW-8/§3.1: only `accepted_recalibrations` and `action_completions` have a
 * producing event today). The other three render disabled with the spec's
 * own "Create a habit first" / "needs weekly review" empty-state language
 * (settings-rewards.md §4's disabled-picker pattern) rather than being
 * hidden outright — the transparency principle applies to what's NOT
 * buildable yet too, same as the XP table's "not yet active" rows.
 */
const USABLE_TEMPLATES: readonly CriterionTemplate[] = ['accepted_recalibrations', 'action_completions'];

const DISABLED_REASON: Partial<Record<CriterionTemplate, string>> = {
  habit_completions: 'Create a habit first — habits are a future round.',
  checkin_streak_weeks: 'Needs the daily check-in flow — a future round.',
  clean_weeks: 'Create an avoid habit first — habits are a future round.',
  weekly_reviews: 'Needs the weekly review screen — a future round.',
};

/**
 * Badge create/edit sheet (settings-rewards.md §4). Scoped to what Rewards
 * v1 can actually build criteria for (RW-8's two live templates); the other
 * three ratified templates still appear in the picker, disabled, with the
 * spec's own "create a habit first"-style empty-state copy, so the option
 * set stays honest about the full R7.10 template list rather than silently
 * shrinking it. Icon picker reuses the shared curated set (§4's own
 * cross-reference: "a user editing a badge and a user editing a level name
 * draw from visually consistent stock").
 */
export function BadgeEditorSheet({
  initialName,
  initialIconId,
  initialCriterion,
  onSave,
  onCancel,
}: {
  readonly initialName?: string;
  readonly initialIconId?: string;
  readonly initialCriterion?: BadgeCriterion;
  readonly onSave: (draft: BadgeDraft) => Promise<void>;
  readonly onCancel: () => void;
}) {
  const [name, setName] = useState(initialName ?? '');
  const [iconId, setIconId] = useState(initialIconId ?? 'star');
  const [template, setTemplate] = useState<CriterionTemplate>(
    initialCriterion?.kind ?? 'action_completions',
  );
  const [target, setTarget] = useState(
    initialCriterion && 'count' in initialCriterion ? initialCriterion.count : 5,
  );
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const canSubmit = name.trim().length > 0 && target > 0 && USABLE_TEMPLATES.includes(template);

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const criterion: BadgeCriterion =
        template === 'accepted_recalibrations'
          ? { kind: 'accepted_recalibrations', count: target }
          : { kind: 'action_completions', count: target };
      await onSave({ name: name.trim(), iconId, criterion });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={initialName ? 'Edit badge' : 'New badge'}
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--surface-base) 70%, transparent)',
        padding: 'var(--space-6)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-overlay)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-2)',
          padding: 'var(--space-6)',
          width: '360px',
          maxWidth: '90vw',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
          {initialName ? 'Edit badge' : 'New badge'}
        </h2>

        <label style={{ display: 'block', marginTop: 'var(--space-4)' }}>
          <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
            Name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 40))}
            placeholder="e.g. Ten Actions Deep"
            style={{
              width: '100%',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-sunken)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
              fontFamily: 'var(--font-family)',
            }}
          />
        </label>

        <label style={{ display: 'block', marginTop: 'var(--space-4)' }}>
          <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
            Criteria template
          </span>
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value as CriterionTemplate)}
            style={{
              width: '100%',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-sunken)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
              fontFamily: 'var(--font-family)',
            }}
          >
            {(Object.keys(TEMPLATE_LABELS) as CriterionTemplate[]).map((kind) => (
              <option key={kind} value={kind} disabled={!USABLE_TEMPLATES.includes(kind)}>
                {TEMPLATE_LABELS[kind]}
                {DISABLED_REASON[kind] ? ` — ${DISABLED_REASON[kind]}` : ''}
              </option>
            ))}
          </select>
          {DISABLED_REASON[template] && (
            <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
              {DISABLED_REASON[template]}
            </p>
          )}
        </label>

        <label style={{ display: 'block', marginTop: 'var(--space-4)' }}>
          <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
            Target count
          </span>
          <input
            type="number"
            min={1}
            max={999}
            value={target}
            onChange={(e) => setTarget(Math.max(1, Math.min(999, Number(e.target.value) || 1)))}
            className="num"
            style={{
              width: '100%',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-sunken)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
              fontFamily: 'var(--font-family)',
            }}
          />
        </label>

        <div style={{ marginTop: 'var(--space-4)', position: 'relative' }}>
          <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
            Icon
          </span>
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
            }}
          >
            {iconId}
          </button>
          {pickerOpen && (
            <IconPickerPopover
              selected={iconId}
              onSelect={(id) => {
                setIconId(id);
                setPickerOpen(false);
              }}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit || saving}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              background: canSubmit && !saving ? 'var(--hue-primary-vivid)' : 'var(--surface-hover)',
              color: canSubmit && !saving ? 'var(--text-on-accent)' : 'var(--text-disabled)',
              fontWeight: 600,
              fontSize: 'var(--type-body-size)',
              border: '1px solid var(--hue-primary-strong)',
            }}
          >
            Save
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              fontSize: 'var(--type-body-size)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
